import { Provider, IAgentRuntime, Memory, State, logger, ProviderResult } from '@elizaos/core';
import {
  ZapperConfig,
  ZapperResponse,
  AppPositionsData,
  DeFiAppInfo,
  DeFiProtocolInfo,
  AppDiscoveryResult,
  AppDiscoveryFilter,
} from '../types';

export const defiProvider: Provider = {
  name: 'defi',
  description: 'Provides DeFi protocol interaction data, app discovery, and position analytics',

  get: async (runtime: IAgentRuntime, message: Memory, state?: State): Promise<ProviderResult> => {
    try {
      const config: ZapperConfig = {
        apiKey: runtime.getSetting('ZAPPER_API_KEY'),
        apiUrl: runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql',
      };

      if (!config.apiKey) {
        logger.error('ZAPPER_API_KEY not found in settings');
        return {
          text: 'DeFi provider not configured - missing API key',
          data: { error: 'API key not configured' },
        };
      }

      // Extract user's wallet address from message or state
      const userAddress = extractAddressFromMessage(message);
      if (!userAddress) {
        logger.debug('defiProvider: No wallet address found, returning neutral context');
        return {
          text: 'DeFi data context available when wallet address is provided',
          data: { provider: 'defiProvider', status: 'neutral' },
        };
      }

      // Fetch comprehensive DeFi data
      const [appPositions, appDiscovery, protocolInfo] = await Promise.allSettled([
        fetchAppPositions(config, userAddress),
        fetchAppDiscovery(config),
        fetchProtocolInfo(config),
      ]);

      // Build context string
      let context = '=== DeFi Protocol Data ===\n';

      // Add app positions data
      if (appPositions.status === 'fulfilled' && appPositions.value) {
        const positions = appPositions.value;
        context += `Portfolio Summary:\n`;
        context += `- Total Value: $${positions.totalValueUSD.toFixed(2)}\n`;
        context += `- Active Positions: ${positions.positionCount}\n`;
        context += `- Active Apps: ${positions.appCount}\n`;
        context += `- Networks: ${positions.networks.join(', ')}\n`;

        if (positions.summary) {
          context += `\nPosition Types:\n`;
          context += `- Staking: $${positions.summary.staking?.toFixed(2) || '0.00'}\n`;
          context += `- Lending: $${positions.summary.lending?.toFixed(2) || '0.00'}\n`;
          context += `- Liquidity: $${positions.summary.liquidity?.toFixed(2) || '0.00'}\n`;
          context += `- Farming: $${positions.summary.farming?.toFixed(2) || '0.00'}\n`;
          context += `- Other: $${positions.summary.other?.toFixed(2) || '0.00'}\n`;
        }

        // Add top apps
        const topApps = positions.apps
          .sort((a, b) => b.totalValueUSD - a.totalValueUSD)
          .slice(0, 5);

        if (topApps.length > 0) {
          context += `\nTop Apps by Value:\n`;
          topApps.forEach((app) => {
            context += `- ${app.appDisplayName}: $${app.totalValueUSD.toFixed(2)} (${app.positions.length} positions)\n`;
          });
        }
      }

      // Add app discovery data
      if (appDiscovery.status === 'fulfilled' && appDiscovery.value) {
        const discovery = appDiscovery.value;
        context += `\n=== Available DeFi Apps ===\n`;
        context += `Total Apps: ${discovery.total}\n`;

        // Group by category
        const categories = discovery.apps.reduce(
          (acc, app) => {
            if (!acc[app.category]) acc[app.category] = [];
            acc[app.category].push(app);
            return acc;
          },
          {} as Record<string, DeFiAppInfo[]>
        );

        Object.entries(categories).forEach(([category, apps]) => {
          context += `\n${category.toUpperCase()}:\n`;
          apps.slice(0, 3).forEach((app) => {
            context += `- ${app.displayName}: ${app.description || 'No description'}\n`;
            if (app.tvl) context += `  TVL: $${(app.tvl / 1000000).toFixed(1)}M\n`;
          });
        });
      }

      // Add protocol information
      if (protocolInfo.status === 'fulfilled' && protocolInfo.value) {
        const protocols = protocolInfo.value;
        context += `\n=== Protocol Statistics ===\n`;
        protocols.slice(0, 5).forEach((protocol) => {
          context += `- ${protocol.displayName}: ${protocol.description || 'No description'}\n`;
          if (protocol.tvl) context += `  TVL: $${(protocol.tvl / 1000000).toFixed(1)}M\n`;
          if (protocol.category) context += `  Category: ${protocol.category}\n`;
        });
      }

      return {
        text: context,
        data: {
          userAddress,
          appPositions: appPositions.status === 'fulfilled' ? appPositions.value : null,
          appDiscovery: appDiscovery.status === 'fulfilled' ? appDiscovery.value : null,
          protocolInfo: protocolInfo.status === 'fulfilled' ? protocolInfo.value : null,
        },
        values: {
          hasPositions: appPositions.status === 'fulfilled' && appPositions.value !== null,
          totalValueUSD:
            appPositions.status === 'fulfilled' && appPositions.value
              ? appPositions.value.totalValueUSD
              : 0,
          positionCount:
            appPositions.status === 'fulfilled' && appPositions.value
              ? appPositions.value.positionCount
              : 0,
          appCount:
            appPositions.status === 'fulfilled' && appPositions.value
              ? appPositions.value.appCount
              : 0,
          networkCount:
            appPositions.status === 'fulfilled' && appPositions.value
              ? appPositions.value.networkCount
              : 0,
        },
      };
    } catch (error) {
      logger.error('defiProvider: Error fetching DeFi data:', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return {
        text: 'DeFi data temporarily unavailable',
        data: {
          provider: 'defiProvider',
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  },
};

function extractAddressFromMessage(message: Memory): string | null {
  const content = message.content;

  // Check if address is directly in content
  if (content.address && typeof content.address === 'string') {
    return content.address;
  }

  // Try to extract from text using regex
  const addressRegex = /0x[a-fA-F0-9]{40}/;
  const match = content.text?.match(addressRegex);
  if (match) {
    return match[0];
  }

  return null;
}

async function fetchAppPositions(
  config: ZapperConfig,
  address: string
): Promise<AppPositionsData | null> {
  try {
    // Map network names to chain IDs (recommended by Zapper API)
    const chainIds = [1, 137, 42161, 10, 8453]; // ethereum, polygon, arbitrum, optimism, base

    const query = `
      query GetAppPositions($addresses: [Address!]!, $chainIds: [Int!]) {
        portfolioV2(addresses: $addresses, chainIds: $chainIds) {
          appBalances {
            byApp(first: 100) {
              edges {
                node {
                  id
                  appId
                  networkId
                  balanceUSD
                  positionCount
                  app {
                    displayName
                    slug
                    imgUrl
                  }
                  network {
                    name
                    slug
                  }
                  positionBalances(first: 100) {
                    edges {
                      node {
                        ... on AppTokenPositionBalance {
                          type
                          address
                          network
                          balance
                          balanceUSD
                          price
                          symbol
                          decimals
                          key
                          appId
                          groupId
                          groupLabel
                        }
                        ... on ContractPositionBalance {
                          type
                          key
                          address
                          network
                          appId
                          groupId
                          groupLabel
                          balanceUSD
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      addresses: [address],
      chainIds: chainIds,
    };

    const response = await fetch(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('defiProvider: fetchAppPositions HTTP error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        url: config.apiUrl,
        address: address,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = (await response.json()) as ZapperResponse<{
      portfolioV2: {
        appBalances: {
          byApp: {
            edges: Array<{
              node: {
                id: string;
                appId: string;
                networkId: string;
                balanceUSD: number;
                positionCount: number;
                app: {
                  displayName: string;
                  slug: string;
                  imgUrl: string;
                };
                network: {
                  name: string;
                  slug: string;
                };
                positionBalances: {
                  edges: Array<{
                    node: {
                      type: string;
                      key?: string;
                      address: string;
                      network: string;
                      appId: string;
                      groupId: string;
                      groupLabel?: string;
                      balanceUSD: number;
                      balance?: string;
                      price?: number;
                      symbol?: string;
                      decimals?: number;
                    };
                  }>;
                };
              };
            }>;
          };
        };
      };
    }>;

    if (data.errors && data.errors.length > 0) {
      logger.error('defiProvider: fetchAppPositions GraphQL errors:', {
        errors: data.errors,
        address: address,
        url: config.apiUrl,
      });
      return null;
    }

    const portfolioData = data.data?.portfolioV2?.appBalances?.byApp;
    if (!portfolioData?.edges || portfolioData.edges.length === 0) {
      return null;
    }

    // Transform the data to match the expected AppPositionsData format
    const appPositions: any[] = [];
    const apps: any[] = [];
    let totalValueUSD = 0;
    const networks = new Set<string>();

    portfolioData.edges.forEach((appEdge) => {
      const appNode = appEdge.node;
      
      // Add network to set
      networks.add(appNode.network.name);
      
      // Process each position balance
      appNode.positionBalances.edges.forEach((positionEdge) => {
        const position = positionEdge.node;
        
        appPositions.push({
          id: position.key || `${position.appId}-${position.groupId}`,
          label: position.groupLabel || position.type,
          symbol: position.symbol || 'N/A',
          type: position.type,
          balanceUSD: position.balanceUSD || 0,
          apr: 0,
          apy: 0,
        });
        
        totalValueUSD += position.balanceUSD || 0;
      });
      
      // Add app info
      apps.push({
        appId: appNode.appId,
        appName: appNode.app.displayName,
        appDisplayName: appNode.app.displayName,
        network: { name: appNode.network.name },
        address: address,
        totalValueUSD: appNode.balanceUSD,
        positions: appNode.positionBalances.edges.map(edge => ({
          id: edge.node.key || `${edge.node.appId}-${edge.node.groupId}`,
          label: edge.node.groupLabel || edge.node.type,
          symbol: edge.node.symbol || 'N/A',
          type: edge.node.type,
          balanceUSD: edge.node.balanceUSD || 0,
          apr: 0,
          apy: 0,
        })),
      });
    });

    return {
      address: address,
      totalValueUSD,
      networks: Array.from(networks),
      positionCount: appPositions.length,
      appCount: apps.length,
      networkCount: networks.size,
      summary: {
        staking: 0,
        lending: 0,
        liquidity: 0,
        farming: 0,
        other: totalValueUSD,
      },
      apps,
    };
  } catch (error) {
    logger.error('defiProvider: Error fetching app positions:', {
      error: error instanceof Error ? error.message : String(error),
      address: address,
      apiUrl: config.apiUrl,
    });
    return null;
  }
}

async function fetchAppDiscovery(
  config: ZapperConfig,
  filter?: AppDiscoveryFilter
): Promise<AppDiscoveryResult | null> {
  try {
    const query = `
      query GetApps($first: Int, $after: String, $orderBy: AppOrderByOption, $orderDirection: OrderDirectionOption, $filters: AppsFiltersArgs) {
        apps(first: $first, after: $after, orderBy: $orderBy, orderDirection: $orderDirection, filters: $filters) {
          edges {
            node {
              id
              databaseId
              slug
              status
              displayName
              description
              url
              imgUrl
              category {
                id
                name
                slug
                description
              }
              tags
              tvl {
                network
                tvl
              }
              createdAt
              links {
                discord
                github
                medium
                telegram
                twitter
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          totalCount
        }
      }
    `;

    const variables = {
      first: 50,
      orderBy: 'NAME' as const,
      orderDirection: 'ASC' as const,
      filters: filter || {},
    };

    const response = await fetch(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('defiProvider: fetchAppDiscovery HTTP error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        url: config.apiUrl,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json() as {
      data?: {
        apps: {
          edges: Array<{
            node: {
              id: string;
              databaseId: number;
              slug: string;
              status: string;
              displayName: string;
              description: string;
              url: string;
              imgUrl: string;
              category: {
                id: string;
                name: string;
                slug: string;
                description: string;
              };
              tags: string[];
              tvl: Array<{
                network: string;
                tvl: number;
              }>;
              createdAt: number;
              links: {
                discord?: string;
                github?: string;
                medium?: string;
                telegram?: string;
                twitter?: string;
              } | null;
            };
          }>;
          pageInfo: {
            hasNextPage: boolean;
            hasPreviousPage: boolean;
            startCursor: string;
            endCursor: string;
          };
          totalCount: number;
        };
      };
      errors?: Array<{ message: string }>;
    };

    if (data.errors && data.errors.length > 0) {
      logger.error('defiProvider: fetchAppDiscovery GraphQL errors:', {
        errors: data.errors,
        url: config.apiUrl,
      });
      return null;
    }

    const appsData = data.data?.apps;
    if (!appsData?.edges) {
      return null;
    }

    // Transform the data to match the expected AppDiscoveryResult format
    const apps = appsData.edges.map((edge) => {
      const node = edge.node;
      const totalTvl = node.tvl.reduce((sum, tvlItem) => sum + tvlItem.tvl, 0);
      
      return {
        appId: node.slug,
        name: node.displayName,
        displayName: node.displayName,
        description: node.description,
        iconUrl: node.imgUrl,
        category: node.category.slug,
        tags: node.tags,
        networks: node.tvl.map(tvlItem => tvlItem.network),
        verified: node.status === 'ENABLED',
        tvl: totalTvl,
        volume24h: 0, // Not available in this query
        users: 0, // Not available in this query
        website: node.url,
        protocols: [node.slug],
        lastUpdated: new Date(node.createdAt).toISOString(),
      };
    });

    return {
      apps,
      total: appsData.totalCount,
      page: 1,
      limit: 50,
      filters: filter || {},
    };
  } catch (error) {
    logger.error('defiProvider: Error fetching app discovery:', {
      error: error instanceof Error ? error.message : String(error),
      apiUrl: config.apiUrl,
    });
    return null;
  }
}

async function fetchProtocolInfo(config: ZapperConfig): Promise<DeFiProtocolInfo[] | null> {
  try {
    const query = `
      query GetProtocols($first: Int, $after: String, $orderBy: AppOrderByOption, $orderDirection: OrderDirectionOption, $filters: AppsFiltersArgs) {
        apps(first: $first, after: $after, orderBy: $orderBy, orderDirection: $orderDirection, filters: $filters) {
          edges {
            node {
              id
              databaseId
              slug
              status
              displayName
              description
              url
              imgUrl
              category {
                id
                name
                slug
                description
              }
              tags
              tvl {
                network
                tvl
              }
              createdAt
              links {
                discord
                github
                medium
                telegram
                twitter
              }
            }
          }
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          totalCount
        }
      }
    `;

    const variables = {
      first: 20,
      orderBy: 'NAME' as const,
      orderDirection: 'ASC' as const,
      filters: {},
    };

    const response = await fetch(config.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-zapper-api-key': config.apiKey,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error('defiProvider: fetchProtocolInfo HTTP error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        url: config.apiUrl,
      });
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json() as {
      data?: {
        apps: {
          edges: Array<{
            node: {
              id: string;
              databaseId: number;
              slug: string;
              status: string;
              displayName: string;
              description: string;
              url: string;
              imgUrl: string;
              category: {
                id: string;
                name: string;
                slug: string;
                description: string;
              };
              tags: string[];
              tvl: Array<{
                network: string;
                tvl: number;
              }>;
              createdAt: number;
              links: {
                discord?: string;
                github?: string;
                medium?: string;
                telegram?: string;
                twitter?: string;
              } | null;
            };
          }>;
          pageInfo: {
            hasNextPage: boolean;
            hasPreviousPage: boolean;
            startCursor: string;
            endCursor: string;
          };
          totalCount: number;
        };
      };
      errors?: Array<{ message: string }>;
    };

    if (data.errors && data.errors.length > 0) {
      logger.error('defiProvider: fetchProtocolInfo GraphQL errors:', {
        errors: data.errors,
        url: config.apiUrl,
      });
      return null;
    }

    const appsData = data.data?.apps;
    if (!appsData?.edges) {
      return null;
    }

    // Transform the data to match the expected DeFiProtocolInfo format
    const protocols = appsData.edges.map((edge) => {
      const node = edge.node;
      const totalTvl = node.tvl.reduce((sum, tvlItem) => sum + tvlItem.tvl, 0);
      
      return {
        protocolId: node.slug,
        name: node.displayName,
        displayName: node.displayName,
        description: node.description,
        iconUrl: node.imgUrl,
        category: node.category.slug,
        networks: node.tvl.map(tvlItem => tvlItem.network),
        tvl: totalTvl,
        volume24h: 0, // Not available in this query
        users: 0, // Not available in this query
        verified: node.status === 'ENABLED',
        lastUpdated: new Date(node.createdAt).toISOString(),
      };
    });

    return protocols;
  } catch (error) {
    logger.error('defiProvider: Error fetching protocol info:', {
      error: error instanceof Error ? error.message : String(error),
      apiUrl: config.apiUrl,
    });
    return null;
  }
}
